import React, { useState, useEffect } from 'react';

import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiRefreshCw, FiImage, FiFolder } from 'react-icons/fi';
import firebaseService from '../../services/firebaseService';
import { useSiteData } from '../../context/DataContext';
import useNotification from '../../hooks/useNotification';
import './AdminOrders.css'; 
import { products as staticProducts, categories as staticCategories } from '../../data/products';

const AdminContent = () => {
    const { products, categories, settings } = useSiteData();
    const { showSuccess, showError } = useNotification();
    const [activeTab, setActiveTab] = useState('categories'); // Start with categories
    const [isSyncing, setIsSyncing] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Selected category to view its products
    const [selectedCategoryForProducts, setSelectedCategoryForProducts] = useState(null);

    // Editing States
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    
    // Sidebar visibility & titles
    const [sidebarSettings, setSidebarSettings] = useState({
        home: { title: 'HOME', visible: true },
        shop: { title: 'SHOP', visible: true },
        customize: { title: 'CUSTOMIZE', visible: true },
        collections: { title: 'COLLECTIONS', visible: true },
        contact: { title: 'PRIVACY', visible: true }
    });

    useEffect(() => {
        if (settings && settings.sidebar) {
            setSidebarSettings(prev => {
                // Handle backwards compatibility where sidebar was just titles
                const newSettings = { ...prev };
                Object.keys(settings.sidebar).forEach(key => {
                    if (typeof settings.sidebar[key] === 'string') {
                        // old format: homeTitle: 'HOME'
                        const baseKey = key.replace('Title', '');
                        if(newSettings[baseKey]) newSettings[baseKey].title = settings.sidebar[key];
                    } else {
                        // new format
                        newSettings[key] = settings.sidebar[key];
                    }
                });
                return newSettings;
            });
        }
    }, [settings]);

    const handleSyncInitialData = async () => {
        if (!window.confirm("Are you sure? This will push all static products and categories to Firebase. Use this only once!")) return;
        setIsSyncing(true);
        try {
            for (let cat of staticCategories) {
                await firebaseService.saveCategory(cat);
            }
            for (let prod of staticProducts) {
                await firebaseService.saveProduct(prod);
            }
            showSuccess('Initial data synced successfully!');
        } catch (error) {
            showError('Failed to sync data');
            console.error(error);
        }
        setIsSyncing(false);
    };

    const handleCleanupDuplicates = async () => {
        if (!window.confirm("This will delete all duplicate items and ORPHANED products (products without categories). Proceed?")) return;
        setIsSyncing(true);
        try {
            // 1. Delete duplicate categories
            const seenCats = new Set();
            for (let cat of categories) {
                if (seenCats.has(cat.name)) {
                    await firebaseService.deleteCategory(cat._dbId);
                } else {
                    seenCats.add(cat.name);
                }
            }
            
            // 2. Delete duplicate products OR products with no existing category
            const seenProds = new Set();
            const validCategoryIds = new Set(categories.map(c => c.id));

            for (let prod of products) {
                const isDuplicate = seenProds.has(prod.name);
                const isOrphaned = !validCategoryIds.has(prod.categoryId);

                if (isDuplicate || isOrphaned) {
                    await firebaseService.deleteProduct(prod._dbId);
                } else {
                    seenProds.add(prod.name);
                }
            }
            showSuccess('Cleanup successful!');
        } catch (error) {
            showError('Failed to clean up items');
        }
        setIsSyncing(false);
    };

    const handleSaveSettings = async () => {
        try {
            await firebaseService.updateSiteSettings({ sidebar: sidebarSettings });
            showSuccess('Sidebar settings updated!');
        } catch (error) {
            showError('Failed to save settings');
        }
    };

    const handleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingImage(true);

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const MAX_SIZE = 800;

                if (width > height) {
                    if (width > MAX_SIZE) {
                        height *= MAX_SIZE / width;
                        width = MAX_SIZE;
                    }
                } else {
                    if (height > MAX_SIZE) {
                        width *= MAX_SIZE / height;
                        height = MAX_SIZE;
                    }
                }
                
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8); // 80% quality JPEG
                
                if (type === 'categories' && editingCategory) {
                    setEditingCategory({ ...editingCategory, image: dataUrl });
                } else if (type === 'products' && editingProduct) {
                    setEditingProduct({ ...editingProduct, image: dataUrl });
                }
                showSuccess('Image uploaded successfully!');
                setUploadingImage(false);
            };
            img.onerror = () => {
                showError('Invalid image file');
                setUploadingImage(false);
            };
            img.src = event.target.result;
        };
        reader.onerror = () => {
            showError('Failed to read file');
            setUploadingImage(false);
        };
        reader.readAsDataURL(file);
    };

    // Product Handlers
    const handleSaveProduct = async (e) => {
        e.preventDefault();
        try {
            await firebaseService.saveProduct(editingProduct);
            showSuccess('Product saved!');
            setEditingProduct(null);
        } catch (error) {
            showError('Failed to save product');
        }
    };

    const handleDeleteProduct = async (dbId) => {
        if (!window.confirm('Delete this product?')) return;
        try {
            await firebaseService.deleteProduct(dbId);
            showSuccess('Product deleted!');
        } catch (error) {
            showError('Failed to delete product');
        }
    };

    const addProductSize = () => {
        setEditingProduct(prev => ({
            ...prev,
            sizes: [...(prev.sizes || []), { name: '', price: 0, dimensions: '' }]
        }));
    };

    const updateProductSize = (index, field, value) => {
        const newSizes = [...(editingProduct.sizes || [])];
        newSizes[index][field] = field === 'price' ? Number(value) : value;
        setEditingProduct({ ...editingProduct, sizes: newSizes });
    };

    const removeProductSize = (index) => {
        const newSizes = [...(editingProduct.sizes || [])];
        newSizes.splice(index, 1);
        setEditingProduct({ ...editingProduct, sizes: newSizes });
    };

    const addProductColor = () => {
        setEditingProduct(prev => ({
            ...prev,
            colors: [...(prev.colors || []), { name: '', hex: '#000000' }]
        }));
    };

    const updateProductColor = (index, field, value) => {
        const newColors = [...(editingProduct.colors || [])];
        newColors[index][field] = value;
        setEditingProduct({ ...editingProduct, colors: newColors });
    };

    const removeProductColor = (index) => {
        const newColors = [...(editingProduct.colors || [])];
        newColors.splice(index, 1);
        setEditingProduct({ ...editingProduct, colors: newColors });
    };

    // Category Handlers
    const handleSaveCategory = async (e) => {
        e.preventDefault();
        try {
            await firebaseService.saveCategory(editingCategory);
            showSuccess('Category saved!');
            setEditingCategory(null);
        } catch (error) {
            showError('Failed to save category');
        }
    };

    const handleDeleteCategory = async (cat) => {
        if (!window.confirm(`Are you sure you want to delete the category "${cat.name}"? This will also delete ALL products inside it!`)) return;
        try {
            // 1. Delete all products belonging to this category
            const associatedProducts = products.filter(p => p.categoryId === cat.id);
            for (let prod of associatedProducts) {
                if (prod._dbId) {
                    await firebaseService.deleteProduct(prod._dbId);
                }
            }

            // 2. Delete the category itself
            await firebaseService.deleteCategory(cat._dbId);
            showSuccess('Category and all its products deleted!');
        } catch (error) {
            showError('Failed to delete category and products');
        }
    };

    // Filter products for the selected category
    const productsToDisplay = selectedCategoryForProducts 
        ? products.filter(p => p.categoryId === selectedCategoryForProducts)
        : [];

    return (
        <div className="admin-orders-container" style={{ padding: '40px', background: 'var(--bg-main)', minHeight: '100vh', color: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
                <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '32px', margin: 0 }}>Content Manager</h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        onClick={handleCleanupDuplicates} 
                        disabled={isSyncing}
                        style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                        Clean Up Duplicates
                    </button>
                    <button 
                        onClick={handleSyncInitialData} 
                        disabled={isSyncing}
                        style={{ background: 'var(--accent-primary)', color: '#000', border: 'none', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                        <FiRefreshCw className={isSyncing ? "spin" : ""} /> Sync Static Data
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
                {['categories', 'products', 'settings'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => {
                            setActiveTab(tab);
                            if (tab !== 'products') setSelectedCategoryForProducts(null);
                        }}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: activeTab === tab ? 'var(--accent-primary)' : 'rgba(255,255,255,0.5)',
                            fontSize: '16px',
                            fontFamily: 'var(--font-nav)',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            padding: '10px 0',
                            borderBottom: activeTab === tab ? '2px solid var(--accent-primary)' : '2px solid transparent',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '30px', borderRadius: '15px' }}>
                    <h2 style={{ marginBottom: '20px', fontFamily: 'var(--font-heading)' }}>Sidebar Navigation Links</h2>
                    <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '20px' }}>Change titles or toggle visibility to remove pages from the sidebar.</p>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '500px' }}>
                        {Object.keys(sidebarSettings).map(key => (
                            <div key={key} style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: 'var(--accent-primary)', textTransform: 'uppercase' }}>{key} Page Title</label>
                                    <input
                                        type="text"
                                        value={sidebarSettings[key].title}
                                        onChange={(e) => setSidebarSettings({ 
                                            ...sidebarSettings, 
                                            [key]: { ...sidebarSettings[key], title: e.target.value } 
                                        })}
                                        style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '5px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                    <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Visible</label>
                                    <input 
                                        type="checkbox" 
                                        checked={sidebarSettings[key].visible}
                                        onChange={(e) => setSidebarSettings({
                                            ...sidebarSettings,
                                            [key]: { ...sidebarSettings[key], visible: e.target.checked }
                                        })}
                                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                        ))}
                        <button onClick={handleSaveSettings} style={{ background: 'var(--accent-primary)', color: '#000', border: 'none', padding: '15px', borderRadius: '5px', cursor: 'pointer', marginTop: '10px', fontWeight: 'bold' }}>
                            <FiSave style={{ marginRight: '8px' }} /> Save Sidebar Settings
                        </button>
                    </div>
                </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
                <div>
                    {!editingCategory ? (
                        <>
                            <button 
                                onClick={() => setEditingCategory({ id: `cat-${Date.now()}`, name: '', image: '', description: '' })}
                                style={{ background: 'var(--accent-primary)', color: '#000', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', marginBottom: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <FiPlus /> Add New Category
                            </button>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                                {categories.map(cat => (
                                    <div key={cat._dbId || cat.id} style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '10px', position: 'relative' }}>
                                        {cat.image && <img src={cat.image} alt={cat.name} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '5px', marginBottom: '15px' }} />}
                                        <h3 style={{ margin: '0 0 10px 0' }}>{cat.name}</h3>
                                        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', margin: '0 0 15px 0' }}>{cat.description}</p>
                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                            <button onClick={() => { setActiveTab('products'); setSelectedCategoryForProducts(cat.id); }} style={{ flex: '1 1 100%', background: 'rgba(52, 152, 219, 0.2)', border: 'none', padding: '8px', color: '#3498db', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                <FiFolder /> Manage Products
                                            </button>
                                            <button onClick={() => setEditingCategory(cat)} style={{ flex: 1, background: 'rgba(255,255,255,0.1)', border: 'none', padding: '8px', color: '#fff', borderRadius: '5px', cursor: 'pointer' }}><FiEdit2 /> Edit</button>
                                            <button onClick={() => handleDeleteCategory(cat)} style={{ flex: 1, background: 'rgba(255,0,0,0.2)', border: 'none', padding: '8px', color: '#ff4444', borderRadius: '5px', cursor: 'pointer' }}><FiTrash2 /> Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '30px', borderRadius: '15px', maxWidth: '600px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                <h2>{editingCategory._dbId ? 'Edit Category' : 'New Category'}</h2>
                                <button onClick={() => setEditingCategory(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '24px' }}><FiX /></button>
                            </div>
                            <form onSubmit={handleSaveCategory} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <input placeholder="Category ID (e.g. clothing, posters)" value={editingCategory.id} onChange={e => setEditingCategory({...editingCategory, id: e.target.value})} required style={inputStyle} />
                                <input placeholder="Category Name" value={editingCategory.name} onChange={e => setEditingCategory({...editingCategory, name: e.target.value})} required style={inputStyle} />
                                
                                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '5px' }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', color: 'var(--accent-primary)' }}>Category Image</label>
                                    {editingCategory.image && <img src={editingCategory.image} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px', marginBottom: '10px' }} />}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <label style={{ background: 'rgba(255,255,255,0.1)', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <FiImage /> {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'categories')} style={{ display: 'none' }} disabled={uploadingImage} />
                                        </label>
                                    </div>
                                </div>

                                <textarea placeholder="Description" value={editingCategory.description} onChange={e => setEditingCategory({...editingCategory, description: e.target.value})} style={{...inputStyle, minHeight: '100px'}} />
                                <button type="submit" style={submitButtonStyle}><FiSave /> Save Category</button>
                            </form>
                        </div>
                    )}
                </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {selectedCategoryForProducts && (
                                <button onClick={() => setSelectedCategoryForProducts(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', padding: '10px', color: '#fff', borderRadius: '5px', cursor: 'pointer' }}>← Back</button>
                            )}
                            <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>
                                {selectedCategoryForProducts 
                                    ? `Products in: ${categories.find(c => c.id === selectedCategoryForProducts)?.name}`
                                    : "All Products / Orphaned"}
                            </h2>
                        </div>
                        <button 
                            onClick={async () => {
                                if (!window.confirm("ARE YOU SURE? This will delete EVERY product in the database!")) return;
                                setIsSyncing(true);
                                for (let p of products) await firebaseService.deleteProduct(p._dbId);
                                showSuccess("All products deleted");
                                setIsSyncing(false);
                            }}
                            style={{ background: '#c0392b', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            Delete All Products
                        </button>
                    </div>

                    {!editingProduct ? (
                        <>
                            {selectedCategoryForProducts && (
                                <button 
                                    onClick={() => setEditingProduct({ id: `prod-${Date.now()}`, categoryId: selectedCategoryForProducts, name: '', price: 0, image: '', inStock: true, sizes: [] })}
                                    style={{ background: 'var(--accent-primary)', color: '#000', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', marginBottom: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    <FiPlus /> Add New Product to this Category
                                </button>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                                {(selectedCategoryForProducts ? productsToDisplay : products).map(prod => (
                                    <div key={prod._dbId || prod.id} style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '10px', position: 'relative', border: !categories.find(c => c.id === prod.categoryId) ? '1px solid #e74c3c' : 'none' }}>
                                        {prod.image && <img src={prod.image} alt={prod.name} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '5px', marginBottom: '15px' }} />}
                                        <h3 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>{prod.name}</h3>
                                        <p style={{ margin: '0 0 5px 0', color: 'var(--accent-primary)', fontWeight: 'bold' }}>{prod.price} EGP</p>
                                        {!categories.find(c => c.id === prod.categoryId) && (
                                            <p style={{ color: '#e74c3c', fontSize: '10px', marginBottom: '10px', fontWeight: 'bold' }}>⚠️ CATEGORY DELETED</p>
                                        )}
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={() => setEditingProduct(prod)} style={{ flex: 1, background: 'rgba(255,255,255,0.1)', border: 'none', padding: '8px', color: '#fff', borderRadius: '5px', cursor: 'pointer' }}><FiEdit2 /> Edit</button>
                                            <button onClick={() => handleDeleteProduct(prod._dbId)} style={{ flex: 1, background: 'rgba(255,0,0,0.2)', border: 'none', padding: '8px', color: '#ff4444', borderRadius: '5px', cursor: 'pointer' }}><FiTrash2 /> Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                            ) : (
                                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '30px', borderRadius: '15px', maxWidth: '800px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <h2>{editingProduct._dbId ? 'Edit Product' : 'New Product'}</h2>
                                        <button onClick={() => setEditingProduct(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '24px' }}><FiX /></button>
                                    </div>
                                    <form onSubmit={handleSaveProduct} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            <input placeholder="Product ID" value={editingProduct.id} onChange={e => setEditingProduct({...editingProduct, id: e.target.value})} required style={inputStyle} />
                                            <input placeholder="Product Name" value={editingProduct.name} onChange={e => setEditingProduct({...editingProduct, name: e.target.value})} required style={inputStyle} />
                                            <select value={editingProduct.categoryId} onChange={e => setEditingProduct({...editingProduct, categoryId: e.target.value})} required style={inputStyle}>
                                                <option value="">Select Category</option>
                                                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                            </select>
                                            <input type="number" placeholder="Base Price" value={editingProduct.price} onChange={e => setEditingProduct({...editingProduct, price: Number(e.target.value)})} required style={inputStyle} />
                                            
                                            <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '5px' }}>
                                                <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', color: 'var(--accent-primary)' }}>Product Image</label>
                                                {editingProduct.image && <img src={editingProduct.image} alt="Preview" style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '5px', marginBottom: '10px' }} />}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                    <label style={{ background: 'rgba(255,255,255,0.1)', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', flex: 1, justifyContent: 'center' }}>
                                                        <FiImage /> {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'products')} style={{ display: 'none' }} disabled={uploadingImage} />
                                                    </label>
                                                </div>
                                            </div>

                                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                                <input type="checkbox" checked={editingProduct.inStock} onChange={e => setEditingProduct({...editingProduct, inStock: e.target.checked})} style={{ width: '20px', height: '20px' }} />
                                                In Stock (Available for purchase)
                                            </label>
                                        </div>

                                        <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '10px' }}>
                                            <h3 style={{ marginTop: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                Options/Sizes
                                                <button type="button" onClick={addProductSize} style={{ background: 'var(--accent-primary)', color: '#000', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>+ Add Option</button>
                                            </h3>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                                                {(editingProduct.sizes || []).map((size, index) => (
                                                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '5px', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '5px' }}>
                                                        <input placeholder="Name (e.g. S, M, L)" value={size.name} onChange={e => updateProductSize(index, 'name', e.target.value)} style={{...inputStyle, padding: '5px', fontSize: '12px'}} />
                                                        <input type="number" placeholder="Price" value={size.price} onChange={e => updateProductSize(index, 'price', e.target.value)} style={{...inputStyle, padding: '5px', fontSize: '12px'}} />
                                                        <input placeholder="Dimensions/Desc" value={size.dimensions || ''} onChange={e => updateProductSize(index, 'dimensions', e.target.value)} style={{...inputStyle, padding: '5px', fontSize: '12px'}} />
                                                        <button type="button" onClick={() => removeProductSize(index)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer' }}><FiTrash2 /></button>
                                                    </div>
                                                ))}
                                            </div>

                                            <h3 style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                Colors
                                                <button type="button" onClick={addProductColor} style={{ background: 'var(--accent-primary)', color: '#000', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>+ Add Color</button>
                                            </h3>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '200px', overflowY: 'auto', paddingRight: '10px' }}>
                                                {(editingProduct.colors || []).map((color, index) => (
                                                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 50px auto', gap: '5px', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '5px' }}>
                                                        <input placeholder="Color Name (e.g. Red)" value={color.name} onChange={e => updateProductColor(index, 'name', e.target.value)} style={{...inputStyle, padding: '5px', fontSize: '12px'}} />
                                                        <input type="color" value={color.hex} onChange={e => updateProductColor(index, 'hex', e.target.value)} style={{ width: '100%', height: '30px', border: 'none', background: 'none', cursor: 'pointer' }} />
                                                        <button type="button" onClick={() => removeProductColor(index)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer' }}><FiTrash2 /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
                                            <button type="submit" style={{...submitButtonStyle, width: '100%'}}><FiSave /> Save Product</button>
                                        </div>
                                    </form>
                                </div>
                    )}
                </div>
            )}
        </div>
    );
};

const inputStyle = {
    width: '100%',
    padding: '12px',
    background: 'rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    borderRadius: '5px',
    fontFamily: 'var(--font-nav)',
    boxSizing: 'border-box'
};

const submitButtonStyle = {
    background: 'var(--accent-primary)',
    color: '#000',
    border: 'none',
    padding: '15px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontSize: '16px'
};

export default AdminContent;
